import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Download, AlertCircle } from 'lucide-react';

function NeedsList({ items }) {
  if (!items) return null;
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="text-sm text-foreground flex items-start gap-2">
          <span className="text-green-600">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function DontNeedList({ items }) {
  if (!items) return null;
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
          <span>•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function SharedCard() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [condition, setCondition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    const loadSharedCard = async () => {
      try {
        // First get the condition ID from the share link
        const shareRes = await fetch(`${API}/share/${shareId}`);
        
        if (shareRes.status === 404) {
          setError('This share link was not found.');
          setLoading(false);
          return;
        }
        
        if (shareRes.status === 410) {
          setError('This share link has expired.');
          setLoading(false);
          return;
        }
        
        if (!shareRes.ok) {
          setError('Failed to load shared card.');
          setLoading(false);
          return;
        }
        
        const shareData = await shareRes.json();
        const conditionId = shareData.condition_id;
        
        // Then load the condition data
        const conditionsRes = await fetch('/conditionsCards.json');
        const conditionsData = await conditionsRes.json();
        
        setCondition(conditionsData[conditionId] || null);
        
        if (!conditionsData[conditionId]) {
          setError('Condition not found.');
        }
      } catch (err) {
        console.error('Error loading shared card:', err);
        setError('Failed to load shared card.');
      } finally {
        setLoading(false);
      }
    };
    
    loadSharedCard();
  }, [shareId, API]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading shared card...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center card-soft max-w-md" data-testid="share-error">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-semibold text-foreground">{error}</h2>
          <p className="text-muted-foreground mt-2">
            The link you followed may be invalid or expired.
          </p>
          <Button onClick={() => navigate('/')} className="mt-6 btn-primary">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  if (!condition) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <h2 className="font-heading text-xl font-semibold">Card Not Found</h2>
          <Button onClick={() => navigate('/')} className="mt-4">Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="shared-card-page">
      <div className="print:hidden max-w-2xl mx-auto px-6 pt-8">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            data-testid="home-btn"
          >
            <ArrowLeft className="h-5 w-5" /><span>Home</span>
          </button>
          <span className="text-xs text-muted-foreground bg-secondary px-3 py-1 rounded-full">
            Shared Card
          </span>
        </div>
        <div className="mt-6 flex gap-3">
          <Button onClick={() => window.print()} className="btn-primary" data-testid="print-btn">
            <Printer className="h-5 w-5 mr-2" />Print Card
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="save-pdf-btn">
            <Download className="h-5 w-5 mr-2" />Save as PDF
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-6 print:p-0">
        <div className="bg-white border-2 border-primary rounded-3xl p-8 print:border print:rounded-lg print:p-6">
          <div className="text-center border-b border-border pb-6 mb-6">
            <h1 className="font-heading text-2xl font-semibold text-primary">{condition.name}</h1>
            <p className="text-muted-foreground mt-1">{condition.description}</p>
            <p className="text-xs text-muted-foreground mt-3">This card helps communicate my needs.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-secondary/30 rounded-2xl p-5">
              <h2 className="font-heading font-semibold text-green-700 mb-3">What Helps Me</h2>
              <NeedsList items={condition.needs} />
            </div>
            <div className="bg-muted/50 rounded-2xl p-5">
              <h2 className="font-heading font-semibold text-muted-foreground mb-3">What I Don't Need to Provide</h2>
              <DontNeedList items={condition.dontNeed} />
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">I have an invisible disability. Accommodations help me succeed.</p>
            <p className="text-xs text-muted-foreground mt-2">Learn more: silent-disabilities.org</p>
          </div>
        </div>

        <div className="mt-6 card-soft print:hidden">
          <h3 className="font-medium text-foreground mb-2">About PwSD Navigator</h3>
          <p className="text-sm text-muted-foreground">
            PwSD Navigator helps people with invisible disabilities navigate disclosure decisions and communicate their needs effectively.
          </p>
          <Button onClick={() => navigate('/')} variant="outline" className="mt-4">
            Learn More
          </Button>
        </div>
      </div>

      <style>{`@media print { .print\\:hidden { display: none !important; } }`}</style>
    </div>
  );
}
